/**
 * Analytics Card Component
 * Multi-view analytics card with three switchable views:
 * - Alert Trends (LineChart)
 * - Product Velocity (BarChart)
 * - Stock Levels (StackedAreaChart)
 * 
 * Features:
 * - Unified filter button with time range and severity options
 * - Applied filters shown as animated removable pills
 * - Summary metrics row for Alert Trends and Stock Levels
 * - Tabs positioned inline with filter button
 * - Asymmetric bar chart illustration in top-right corner
 */

import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Box,
  SkeletonDisplayText,
  Icon,
  Tabs,
  Button,
  Popover,
  ChoiceList,
  Tag,
  Tooltip,
  Divider,
} from '@shopify/polaris';
import { ChartVerticalIcon, AlertTriangleIcon, AlertCircleIcon, InfoIcon, CalendarIcon, FilterIcon } from '@shopify/polaris-icons';
import { useSearchParams } from 'react-router';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  AlertTrendsView,
  VelocityView,
  StockLevelsView,
  useTooltipPositionCorrection,
} from './analytics';
import type {
  AnalyticsView,
  VelocityAnalyticsItem,
  InventoryTrendItem,
  AnalyticsCardProps,
  VelocityAnalyticsResponse,
  InventoryTrendsResponse,
  AlertTrendsSummary,
  StockLevelsSummary,
  TimeRangePreset,
  AlertSeverityFilter,
} from '../../types/analytics';
import type { ChartDataPoint } from '../../types/dashboard';
import { AnalyticsSummaryMetrics } from './AnalyticsSummaryMetrics';
import { authenticatedFetch } from '~/utils/authenticated-fetch';

/**
 * Severity badge configuration for tabs row
 */
type SpecificSeverity = 'critical' | 'warning' | 'info';

/**
 * Configuration for severity filter badges
 * Maps severity level to icon, label, and tone for visual display
 */
const SEVERITY_CONFIG: Record<SpecificSeverity, { icon: typeof AlertTriangleIcon; label: string; tone: 'critical' | 'warning' | 'info' }> = {
  critical: { icon: AlertTriangleIcon, label: 'Critical', tone: 'critical' },
  warning: { icon: AlertCircleIcon, label: 'Warning', tone: 'warning' },
  info: { icon: InfoIcon, label: 'Info', tone: 'info' },
};

/**
 * Time range options for the filter
 */
const TIME_RANGE_OPTIONS = [
  { value: '7' as const, label: 'Last 7 days', shortLabel: '7d' },
  { value: '14' as const, label: 'Last 14 days', shortLabel: '14d' },
  { value: '30' as const, label: 'Last 30 days', shortLabel: '30d' },
];

/**
 * Get subtitle text based on active view and time range
 */
function getSubtitleText(view: AnalyticsView, days: string | null): string {
  // No time range selected
  if (days === null) {
    return 'Select a time range to view analytics';
  }
  
  switch (view) {
    case 'alerts':
      switch (days) {
        case '7': return 'Weekly overview of inventory alerts';
        case '14': return 'Two-week overview of inventory alerts';
        case '30': return 'Monthly overview of inventory alerts';
        default: return 'Overview of inventory alerts';
      }
    case 'velocity':
      switch (days) {
        case '7': return 'Top products by sales velocity (7-day average)';
        case '14': return 'Top products by sales velocity (14-day average)';
        case '30': return 'Top products by sales velocity (30-day average)';
        default: return 'Top products by sales velocity';
      }
    case 'inventory':
      switch (days) {
        case '7': return 'Inventory health trends over 7 days';
        case '14': return 'Inventory health trends over 14 days';
        case '30': return 'Inventory health trends over 30 days';
        default: return 'Inventory health trends';
      }
  }
}

/**
 * Calculate alert trends summary from chart data
 */
function calculateAlertSummary(data: ChartDataPoint[], _days: string): AlertTrendsSummary {
  const totalCritical = data.reduce((sum, d) => sum + d.critical, 0);
  const totalWarning = data.reduce((sum, d) => sum + d.warning, 0);
  const totalInfo = data.reduce((sum, d) => sum + d.info, 0);
  const totalAlerts = totalCritical + totalWarning + totalInfo;

  // Find peak alert day
  let peakDay: string | null = null;
  let peakCount = 0;
  for (const d of data) {
    const dayTotal = d.critical + d.warning + d.info;
    if (dayTotal > peakCount) {
      peakCount = dayTotal;
      peakDay = d.date;
    }
  }

  return {
    totalAlerts,
    criticalCount: totalCritical,
    warningCount: totalWarning,
    infoCount: totalInfo,
    criticalChange: 0, // Would need comparison data
    warningChange: 0,
    peakAlertDay: peakDay,
    peakAlertCount: peakCount,
    averageAlertsPerDay: data.length > 0 ? totalAlerts / data.length : 0,
    resolvedAlerts: 0, // Would need resolution data
  };
}

/**
 * Calculate stock levels summary from inventory trends response
 */
function calculateStockSummary(response: InventoryTrendsResponse): StockLevelsSummary {
  const { summary } = response;
  const totalUnits = summary.totalCritical + summary.totalWarning + summary.totalHealthy;
  const totalSKUs = response.items.length > 0 ? response.items[response.items.length - 1].critical + 
    response.items[response.items.length - 1].warning + 
    response.items[response.items.length - 1].healthy : 0;

  return {
    totalSKUs,
    healthyPercentage: totalUnits > 0 ? (summary.totalHealthy / totalUnits) * 100 : 0,
    warningPercentage: totalUnits > 0 ? (summary.totalWarning / totalUnits) * 100 : 0,
    criticalPercentage: totalUnits > 0 ? (summary.totalCritical / totalUnits) * 100 : 0,
    totalUnits,
    healthyUnits: summary.totalHealthy,
    warningUnits: summary.totalWarning,
    criticalUnits: summary.totalCritical,
    outOfStockCount: 0, // Would need separate data
    lowStockCount: 0, // Would need separate data
    healthyChange: 0, // Would need comparison data
    criticalChange: 0,
  };
}

/**
 * AnalyticsCard - Multi-view analytics with Alert Trends, Product Velocity, and Stock Levels
 * Features:
 * - Three switchable views via Tabs
 * - Enhanced filter bar with time range and severity filters
 * - Summary metrics row for alerts and stock levels
 * - Parallel data fetching when time range changes
 * - Built-in accessibility via polaris-viz
 * - Loading and error states
 */
export function AnalyticsCard({
  initialAlertTrends,
  initialVelocityData,
  initialInventoryTrends,
  defaultTimeRange = '7',
  alertSummary: initialAlertSummary,
  stockSummary: initialStockSummary,
}: AnalyticsCardProps) {
  const [activeView, setActiveView] = useState<AnalyticsView>('alerts');
  const [timeRange, setTimeRange] = useState<TimeRangePreset>(defaultTimeRange);
  // Default to all severity types selected
  const [severityFilter, setSeverityFilter] = useState<AlertSeverityFilter[]>(['critical', 'warning', 'info']);
  const [alertTrendsData, setAlertTrendsData] = useState<ChartDataPoint[]>(initialAlertTrends || []);
  const [velocityData, setVelocityData] = useState<VelocityAnalyticsItem[]>(initialVelocityData || []);
  const [inventoryTrendsData, setInventoryTrendsData] = useState<InventoryTrendItem[]>(initialInventoryTrends || []);
  const [alertSummary, setAlertSummary] = useState<AlertTrendsSummary | undefined>(initialAlertSummary);
  const [stockSummary, setStockSummary] = useState<StockLevelsSummary | undefined>(initialStockSummary);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [searchParams] = useSearchParams();
  const abortControllerRef = useRef<AbortController | null>(null);
  const chartContainerRef = useRef<HTMLDivElement | null>(null);

  // Apply tooltip position correction for Shopify embedded app iframe context
  useTooltipPositionCorrection(chartContainerRef);

  // Calculate filter count for badge (only count severity filter deviations from default)
  // Time range is always shown as a pill now, so it doesn't count as a "filter"
  const filterCount = useMemo(() => {
    let count = 0;
    // Count deselected severity filters (default is all 3 selected)
    const deselectedCount = 3 - severityFilter.length;
    if (deselectedCount > 0) count += deselectedCount;
    return count;
  }, [severityFilter]);

  // Build API URL with preserved query params
  const buildApiUrl = useCallback((endpoint: string, days: string, severity?: AlertSeverityFilter[]) => {
    const queryString = searchParams.toString();
    let url = `${endpoint}${queryString ? `?${queryString}&` : '?'}days=${days}`;
    if (severity && severity.length > 0) {
      url += `&severity=${severity.join(',')}`;
    }
    return url;
  }, [searchParams]);

  // Fetch all analytics data in parallel when time range changes
  const fetchAllData = useCallback(async (days: string | null, severity?: AlertSeverityFilter[]) => {
    // Skip fetching if no time range selected
    if (days === null) {
      setIsLoading(false);
      return;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setIsLoading(true);
    setHasError(false);

    try {
      const [alertsRes, velocityRes, inventoryRes] = await Promise.all([
        authenticatedFetch(buildApiUrl('/api/alerts/trends', days, severity), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          signal: abortControllerRef.current.signal,
        }),
        authenticatedFetch(buildApiUrl('/api/analytics/velocity', days), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          signal: abortControllerRef.current.signal,
        }),
        authenticatedFetch(buildApiUrl('/api/analytics/inventory', days), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          signal: abortControllerRef.current.signal,
        }),
      ]);

      // Check all responses
      if (!alertsRes.ok || !velocityRes.ok || !inventoryRes.ok) {
        throw new Error('API error');
      }

      const [alertsData, velocityDataResult, inventoryDataResult] = await Promise.all([
        alertsRes.json() as Promise<{ success: boolean; data: ChartDataPoint[]; summary?: AlertTrendsSummary }>,
        velocityRes.json() as Promise<VelocityAnalyticsResponse>,
        inventoryRes.json() as Promise<InventoryTrendsResponse & { stockSummary?: StockLevelsSummary }>,
      ]);

      // Update state with fetched data
      if (alertsData.success && alertsData.data) {
        setAlertTrendsData(alertsData.data);
        // Update alert summary if provided
        if (alertsData.summary) {
          setAlertSummary(alertsData.summary);
        } else {
          // Calculate summary from data
          const summary = calculateAlertSummary(alertsData.data, days);
          setAlertSummary(summary);
        }
      }
      if (velocityDataResult.items) {
        setVelocityData(velocityDataResult.items);
      }
      if (inventoryDataResult.items) {
        setInventoryTrendsData(inventoryDataResult.items);
        // Update stock summary
        if (inventoryDataResult.summary) {
          const summary = calculateStockSummary(inventoryDataResult);
          setStockSummary(summary);
        }
      }
    } catch (error) {
      // Ignore abort errors
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      // Fetch error - set error state for UI feedback
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [buildApiUrl, authenticatedFetch]);

  // Handle time range change - fetch all views
  const handleTimeRangeChange = useCallback((value: TimeRangePreset) => {
    setTimeRange(value);
    if (value !== 'custom') {
      fetchAllData(value, severityFilter.length > 0 ? severityFilter : undefined);
    }
  }, [fetchAllData, severityFilter]);

  // Handle severity filter change
  const handleSeverityFilterChange = useCallback((values: AlertSeverityFilter[]) => {
    setSeverityFilter(values);
    fetchAllData(timeRange, values.length > 0 ? values : undefined);
  }, [fetchAllData, timeRange]);

  // Handle clear filters - reset to defaults (all severity types selected)
  const handleClearFilters = useCallback(() => {
    setTimeRange('7');
    setSeverityFilter(['critical', 'warning', 'info']);
    fetchAllData('7');
  }, [fetchAllData]);

  // Toggle individual severity filter (for badge buttons)
  const toggleSeverityFilter = useCallback((severity: AlertSeverityFilter) => {
    setSeverityFilter(prev => {
      const isSelected = prev.includes(severity);
      let newFilter: AlertSeverityFilter[];
      if (isSelected) {
        // Removing - but ensure at least one remains
        newFilter = prev.filter(s => s !== severity);
        if (newFilter.length === 0) {
          newFilter = [severity]; // Keep at least one
        }
      } else {
        // Adding
        newFilter = [...prev, severity];
      }
      fetchAllData(timeRange, newFilter);
      return newFilter;
    });
  }, [fetchAllData, timeRange]);

  // Track exiting filters for animation
  const [exitingFilters, setExitingFilters] = useState<Set<string>>(new Set());

  // Remove a severity filter with exit animation
  const removeSeverityFilter = useCallback((severity: AlertSeverityFilter) => {
    // Add to exiting set for animation
    setExitingFilters(prev => new Set(prev).add(`severity-${severity}`));
    
    // After animation, remove from state
    setTimeout(() => {
      setSeverityFilter(prev => {
        const newFilter = prev.filter(s => s !== severity);
        // If removing would leave empty, keep all selected
        if (newFilter.length === 0) {
          return ['critical', 'warning', 'info'];
        }
        fetchAllData(timeRange, newFilter);
        return newFilter;
      });
      setExitingFilters(prev => {
        const next = new Set(prev);
        next.delete(`severity-${severity}`);
        return next;
      });
    }, 150); // Match animation duration
  }, [fetchAllData, timeRange]);

  // Remove time range filter (set to null to show disabled state)
  const removeTimeRangeFilter = useCallback(() => {
    setExitingFilters(prev => new Set(prev).add('timeRange'));
    
    setTimeout(() => {
      setTimeRange(null);
      // Don't fetch data when null - chart will show disabled state
      setExitingFilters(prev => {
        const next = new Set(prev);
        next.delete('timeRange');
        return next;
      });
    }, 150);
  }, []);

  // Time range popover state
  const [timeRangePopoverActive, setTimeRangePopoverActive] = useState(false);
  const toggleTimeRangePopover = useCallback(() => {
    setTimeRangePopoverActive(prev => !prev);
  }, []);
  const closeTimeRangePopover = useCallback(() => {
    setTimeRangePopoverActive(false);
  }, []);

  // Unified filter popover state
  const [filterPopoverActive, setFilterPopoverActive] = useState(false);
  const [pendingTimeRange, setPendingTimeRange] = useState<TimeRangePreset | null>(timeRange);
  const [pendingSeverity, setPendingSeverity] = useState<AlertSeverityFilter[]>(severityFilter);

  const toggleFilterPopover = useCallback(() => {
    setFilterPopoverActive(prev => {
      if (!prev) {
        // Opening - sync pending state with current values
        setPendingTimeRange(timeRange);
        setPendingSeverity(severityFilter);
      }
      return !prev;
    });
  }, [timeRange, severityFilter]);

  const closeFilterPopover = useCallback(() => {
    setFilterPopoverActive(false);
  }, []);

  // Apply filters from popover
  const handleApplyFilters = useCallback(() => {
    setTimeRange(pendingTimeRange);
    setSeverityFilter(pendingSeverity);
    // Only fetch if time range is selected (not null)
    if (pendingTimeRange !== null) {
      fetchAllData(pendingTimeRange, pendingSeverity.length > 0 ? pendingSeverity : undefined);
    }
    closeFilterPopover();
  }, [pendingTimeRange, pendingSeverity, fetchAllData, closeFilterPopover]);

  // Check if pending values differ from current applied values
  const hasFilterChanges = useMemo(() => {
    if (pendingTimeRange !== timeRange) return true;
    if (pendingSeverity.length !== severityFilter.length) return true;
    const sortedPending = [...pendingSeverity].sort();
    const sortedCurrent = [...severityFilter].sort();
    return !sortedPending.every((val, idx) => val === sortedCurrent[idx]);
  }, [pendingTimeRange, timeRange, pendingSeverity, severityFilter]);

  // Calculate which filters are active to show as pills
  const activeFilterPills = useMemo(() => {
    const pills: { key: string; label: string; icon?: typeof CalendarIcon; onRemove: () => void }[] = [];
    
    // Time range - always show pill when a time range is selected
    if (timeRange !== null) {
      const option = TIME_RANGE_OPTIONS.find(o => o.value === timeRange);
      pills.push({
        key: 'timeRange',
        label: option?.label || `${timeRange} days`,
        icon: CalendarIcon,
        onRemove: removeTimeRangeFilter,
      });
    }
    
    // Severity - always show pills for selected severities
    severityFilter.forEach(severity => {
      const config = SEVERITY_CONFIG[severity as SpecificSeverity];
      if (config) {
        pills.push({
          key: `severity-${severity}`,
          label: config.label,
          icon: config.icon,
          onRemove: () => removeSeverityFilter(severity),
        });
      }
    });
    
    return pills;
  }, [timeRange, severityFilter, removeTimeRangeFilter, removeSeverityFilter]);

  // Handle view change - instant, uses cached data
  const handleViewChange = useCallback((view: AnalyticsView) => {
    setActiveView(view);
  }, []);

  // Handle tab selection
  const handleTabChange = useCallback((selectedTabIndex: number) => {
    const views: AnalyticsView[] = ['alerts', 'velocity', 'inventory'];
    setActiveView(views[selectedTabIndex]);
  }, []);

  // Get active tab index
  const selectedTabIndex = useMemo(() => {
    const views: AnalyticsView[] = ['alerts', 'velocity', 'inventory'];
    return views.indexOf(activeView);
  }, [activeView]);

  // Tabs configuration
  const tabs = useMemo(() => [
    { id: 'alerts', content: 'Alert Trends', accessibilityLabel: 'Alert Trends' },
    { id: 'velocity', content: 'Product Velocity', accessibilityLabel: 'Product Velocity' },
    { id: 'inventory', content: 'Stock Levels', accessibilityLabel: 'Stock Levels' },
  ], []);

  /**
   * Initialize analytics data on mount.
   *
   * INTENTIONAL SUPPRESSION: This effect must run exactly once on mount.
   * - `fetchAllData` is excluded because it's a useCallback that changes
   *   identity when state changes. Including it would cause infinite loops.
   * - `timeRange` and initial data props are excluded because they represent
   *   initial configuration. User-driven time range changes are handled by
   *   the filter popover's apply button, not by this effect.
   * - `calculateAlertSummary` and `setAlertSummary` are stable references.
   *
   * This ensures the component fetches fresh data once on mount regardless
   * of what initial data was provided via SSR.
   */
  useEffect(() => {
    const hasInitialData = (initialAlertTrends && initialAlertTrends.length > 0) ||
      (initialVelocityData && initialVelocityData.length > 0) ||
      (initialInventoryTrends && initialInventoryTrends.length > 0);

    // Use default time range ('7') for initial fetch if timeRange is null
    const effectiveTimeRange = timeRange ?? '7';

    if (!hasInitialData) {
      // Fetch with all severity types selected (default)
      fetchAllData(effectiveTimeRange, ['critical', 'warning', 'info']);
    } else {
      // Calculate initial summaries from provided data
      if (initialAlertTrends && initialAlertTrends.length > 0 && !initialAlertSummary) {
        const summary = calculateAlertSummary(initialAlertTrends, effectiveTimeRange);
        setAlertSummary(summary);
      }
      // Always fetch fresh data on mount to ensure summaries are populated
      // This ensures Stock Levels metrics are populated even with initial data
      fetchAllData(effectiveTimeRange, ['critical', 'warning', 'info']);
    }

    // Cleanup on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render the appropriate chart based on active view
  const renderChart = () => {
    // No time range selected - show disabled state with CTA
    if (timeRange === null) {
      return (
        <div className="analytics-disabled-state">
          <Box
            padding="800"
            background="bg-surface-secondary"
            borderRadius="300"
          >
            <BlockStack gap="400" align="center">
              <div style={{ opacity: 0.5 }}>
                <Icon source={CalendarIcon} tone="subdued" />
              </div>
              <BlockStack gap="200" align="center">
                <Text as="p" variant="bodyMd" fontWeight="semibold" alignment="center">
                  No time range selected
                </Text>
                <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                  Select a time range from the filters to view analytics data
                </Text>
              </BlockStack>
              <Button
                onClick={toggleFilterPopover}
                icon={FilterIcon}
                size="slim"
              >
                Select time range
              </Button>
            </BlockStack>
          </Box>
        </div>
      );
    }

    if (isLoading) {
      return (
        <BlockStack gap="400">
          <SkeletonDisplayText size="small" />
          <Box minHeight="320px" background="bg-surface-secondary" borderRadius="200" />
        </BlockStack>
      );
    }

    if (hasError) {
      return (
        <div className="analytics-content-enter">
          <Box
            padding="400"
            background="bg-surface-critical"
            borderRadius="200"
          >
            <BlockStack gap="200" align="center">
              <Text as="p" tone="critical" alignment="center">
                Failed to load analytics data
              </Text>
              <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                Please try again or contact support if the problem persists
              </Text>
            </BlockStack>
          </Box>
        </div>
      );
    }

    switch (activeView) {
      case 'alerts':
        return <AlertTrendsView data={alertTrendsData} />;
      case 'velocity':
        return <VelocityView data={velocityData} />;
      case 'inventory':
        return <StockLevelsView data={inventoryTrendsData} />;
    }
  };

  return (
    <div className="analytics-card">
      <Card>
        <BlockStack gap="400">
          {/* Header Row: Title + Filters (matching monitoring center pattern) */}
          <InlineStack align="space-between" blockAlign="center">
            {/* Left side: Icon and Title */}
            <InlineStack gap="200" blockAlign="center">
              <Icon source={ChartVerticalIcon} tone="subdued" />
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd" fontWeight="semibold">
                  Analytics
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {getSubtitleText(activeView, timeRange)}
                </Text>
              </BlockStack>
            </InlineStack>

            {/* Right side: Filters */}
            <InlineStack gap="200" blockAlign="center">
              {/* Active filter pills - animated entrance/exit */}
              <InlineStack gap="100" blockAlign="center">
                {activeFilterPills.map((pill) => {
                  const isExiting = exitingFilters.has(pill.key);
                  
                  return (
                    <div 
                      key={pill.key}
                      className={`analytics-filter-chip${isExiting ? ' analytics-filter-chip--exiting' : ''}`}
                    >
                      <Tag onRemove={pill.onRemove}>
                        <InlineStack gap="100" blockAlign="center" wrap={false}>
                          {pill.icon && <Icon source={pill.icon} tone="subdued" />}
                          <span>{pill.label}</span>
                        </InlineStack>
                      </Tag>
                    </div>
                  );
                })}
              </InlineStack>

              {/* Unified filter button */}
              <Popover
                active={filterPopoverActive}
                activator={
                  <Tooltip content="Filter options">
                    <div style={{ position: 'relative', display: 'inline-flex' }}>
                      <Button
                        onClick={toggleFilterPopover}
                        icon={FilterIcon}
                        size="slim"
                        disabled={isLoading}
                        pressed={filterPopoverActive}
                        accessibilityLabel={`Filter options${filterCount > 0 ? ` - ${filterCount} active` : ''}`}
                      >
                        Filters
                      </Button>
                      {filterCount > 0 && (
                        <span
                          style={{
                            position: 'absolute',
                            top: '-6px',
                            right: '-8px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '18px',
                            height: '18px',
                            padding: '0 5px',
                            fontSize: '10px',
                            fontWeight: 600,
                            color: '#fff',
                            backgroundColor: '#8b7bd8',
                            borderRadius: '9px',
                            border: '2px solid white',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                          }}
                        >
                          {filterCount}
                        </span>
                      )}
                    </div>
                  </Tooltip>
                }
                onClose={closeFilterPopover}
                preferredPosition="below"
                preferredAlignment="right"
                autofocusTarget="first-node"
              >
                <Box padding="300" minWidth="260px">
                  <BlockStack gap="400">
                    {/* Time Range Section */}
                    <BlockStack gap="200">
                      <div className="analytics-filter-section-header-wrapper">
                        <InlineStack gap="200" blockAlign="center" align="start">
                          <Icon source={CalendarIcon} tone="subdued" />
                          <Text as="span" variant="headingSm" fontWeight="semibold">
                            Time Range
                          </Text>
                        </InlineStack>
                      </div>
                      <ChoiceList
                        title="Select time range"
                        titleHidden
                        choices={TIME_RANGE_OPTIONS.map(opt => ({
                          value: opt.value,
                          label: opt.label,
                        }))}
                        selected={pendingTimeRange ? [pendingTimeRange] : []}
                        onChange={(values) => {
                          // If clicking already-selected option, deselect it (set to null)
                          if (values.length === 0 || (values[0] === pendingTimeRange)) {
                            setPendingTimeRange(null);
                          } else {
                            setPendingTimeRange(values[0] as TimeRangePreset);
                          }
                        }}
                      />
                    </BlockStack>

                    <Divider />

                    {/* Severity Filter Section */}
                    <BlockStack gap="200">
                      <div className="analytics-filter-section-header-wrapper">
                        <InlineStack gap="200" blockAlign="center" align="start">
                          <Icon source={FilterIcon} tone="subdued" />
                          <Text as="span" variant="headingSm" fontWeight="semibold">
                            Alert Severity
                          </Text>
                        </InlineStack>
                      </div>
                      <ChoiceList
                        title="Filter by severity"
                        titleHidden
                        choices={[
                          { value: 'critical', label: 'Critical' },
                          { value: 'warning', label: 'Warning' },
                          { value: 'info', label: 'Info' },
                        ]}
                        selected={pendingSeverity}
                        onChange={(values) => setPendingSeverity(values as AlertSeverityFilter[])}
                        allowMultiple
                      />
                    </BlockStack>

                    {/* Actions */}
                    <Divider />
                    <InlineStack align="space-between" blockAlign="center">
                      <Button
                        onClick={handleClearFilters}
                        disabled={filterCount === 0}
                        size="slim"
                        variant="plain"
                      >
                        Reset to defaults
                      </Button>
                      <Button
                        onClick={handleApplyFilters}
                        size="slim"
                        variant="primary"
                        disabled={!hasFilterChanges}
                      >
                        Apply
                      </Button>
                    </InlineStack>
                  </BlockStack>
                </Box>
              </Popover>
            </InlineStack>
          </InlineStack>

          {/* Tabs - rendered directly without wrapper (matches MonitoringCenter pattern) */}
          <Tabs
            tabs={[
              {
                id: 'alerts',
                content: 'Alert Trends',
                accessibilityLabel: 'Alert Trends',
                panelID: 'alerts-panel',
              },
              {
                id: 'velocity',
                content: 'Product Velocity',
                accessibilityLabel: 'Product Velocity',
                panelID: 'velocity-panel',
              },
              {
                id: 'inventory',
                content: 'Stock Levels',
                accessibilityLabel: 'Stock Levels',
                panelID: 'inventory-panel',
              },
            ]}
            selected={activeView === 'alerts' ? 0 : activeView === 'velocity' ? 1 : 2}
            onSelect={handleTabChange}
            disabled={isLoading}
          />

          {/* Summary metrics row - conditionally rendered based on view and time range */}
          {timeRange !== null && (
            <AnalyticsSummaryMetrics
              activeView={activeView}
              alertSummary={alertSummary}
              stockSummary={stockSummary}
              isLoading={isLoading}
              timeRange={timeRange}
            />
          )}

          {/* Chart area - overflow visible for tooltip positioning */}
          <div
            ref={chartContainerRef}
            className="chart-container-wrapper"
            style={{
              minHeight: activeView === 'velocity' ? 'auto' : '360px',
              height: activeView === 'velocity' ? 'auto' : '360px',
              position: 'relative',
              overflow: 'visible',
            }}
          >
            {renderChart()}
          </div>
        </BlockStack>
      </Card>
    </div>
  );
}
